// Single source of truth for per-route SEO metadata.
// Used at runtime by useSEO (SPA navigation) and at build time by
// scripts/prerender.mjs (static HTML emitted for every route).

import { statsArticles, StatsArticle } from "@/data/statsArticles";
import { states, StateInfo } from "@/data/stateData";
import { faqItems, FaqItem } from "@/data/faqItems";
import { idealWomanFaq, datingStandardsFaq, statesFaq } from "@/data/landingFaqs";

export const SITE_URL = "https://femalepartnercalculator.com";
export const SITE_NAME = "Female Partner Calculator";

export interface RouteMeta {
  path: string;
  title: string;
  description: string;
  keywords?: string;
  structuredData?: object;
  /**
   * Emit `noindex, follow` and leave the route out of sitemap.xml. Use for
   * templated or utility pages that don't carry enough unique content to rank.
   */
  noindex?: boolean;
  /** sitemap.xml hints. `lastmod` is YYYY-MM-DD; defaults to the build date. */
  lastmod?: string;
  changefreq?: string;
  priority?: number;
}

function faqPageSchema(items: FaqItem[]): object {
  return {
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };
}

const staticRoutes: RouteMeta[] = [
  {
    path: "/",
    title: "Female Partner Calculator | How Many Women Meet Your Standards?",
    description:
      "See how many US women actually meet your standards. An evidence-based ideal woman calculator powered by real US Census, CDC, and Pew Research data.",
    keywords:
      "female partner calculator, female delusion calculator, ideal woman calculator, how many women meet my standards, dating standards calculator, dating compatibility calculator, partner probability tool, census statistics, statistical dating",
    changefreq: "weekly",
    priority: 1.0,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Female Partner Calculator",
      description:
        "Statistical tool calculating probability of finding compatible female partners using US Census and demographic data",
      url: `${SITE_URL}/`,
      applicationCategory: "UtilityApplication",
      operatingSystem: "Any",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      creator: { "@type": "Organization", name: "Female Partner Calculator" },
      featureList: [
        "Statistical compatibility calculation",
        "US Census data integration",
        "Demographic analysis",
        "Visual probability representation",
        "Reality Score with shareable results",
      ],
    },
  },
  {
    path: "/male-partner-calculator",
    title: "Male Partner Calculator | How Many Men Meet Your Standards?",
    description:
      "How many US men meet your dating standards? Use real Census and CDC data to calculate the probability of finding a male partner by height, income, education, and more.",
    keywords:
      "male partner calculator, how many men meet your standards, how many men meet my standards, male delusion calculator, male dating pool calculator, men height income statistics",
    changefreq: "weekly",
    priority: 0.95,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Male Partner Calculator",
      description:
        "Statistical tool calculating the probability of finding a compatible male partner in the United States by height, income, education, and demographic criteria",
      url: `${SITE_URL}/male-partner-calculator`,
      applicationCategory: "LifestyleApplication",
      operatingSystem: "Web Browser",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    },
  },
  {
    path: "/ideal-woman-calculator",
    title: "Ideal Woman Calculator | What Percentage of Women Fit Your Type?",
    description:
      "Find out what percentage of US women match your ideal type. Filter by age, height, body type, income, and education to see your real odds with Census and CDC data.",
    keywords:
      "ideal woman calculator, ideal girl calculator, my ideal woman calculator, ideal woman probability calculator, what percentage of women fit my type, how rare is my type of girl",
    changefreq: "weekly",
    priority: 0.9,
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebApplication",
          name: "Ideal Woman Calculator",
          description:
            "Statistical tool estimating what percentage of US women match your criteria for age, height, body type, income, and education using US Census and CDC data",
          url: `${SITE_URL}/ideal-woman-calculator`,
          applicationCategory: "LifestyleApplication",
          operatingSystem: "Web Browser",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
        faqPageSchema(idealWomanFaq),
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Ideal Woman Calculator", path: "/ideal-woman-calculator" },
        ]),
      ],
    },
  },
  {
    path: "/dating-standards-calculator",
    title: "Dating Standards Calculator | Test How Many People Meet Your Standards",
    description:
      "Test your dating standards against real US data. See what percentage of men or women meet your criteria with a free standards test backed by Census, CDC, and Pew research.",
    keywords:
      "dating standards calculator, dating standards test, meet your standards test, how many people meet your standards, male and female standards calculator, probability of finding a partner test, standards test website",
    changefreq: "weekly",
    priority: 0.9,
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebApplication",
          name: "Dating Standards Calculator",
          description:
            "Statistical tool testing how realistic your dating standards are by calculating the probability that a US partner meets your combined criteria",
          url: `${SITE_URL}/dating-standards-calculator`,
          applicationCategory: "LifestyleApplication",
          operatingSystem: "Web Browser",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
        faqPageSchema(datingStandardsFaq),
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Dating Standards Calculator", path: "/dating-standards-calculator" },
        ]),
      ],
    },
  },
  {
    path: "/faq",
    title: "Female Partner Calculator FAQ | Accuracy, How It Works & Common Questions",
    description:
      "Is the calculator accurate? Why are the odds so low? How does it work? Answers to the most common questions about the female partner calculator and its results.",
    keywords:
      "female partner calculator FAQ, dating calculator accuracy, how does the dating calculator work, dating standards test questions, compatibility calculator help",
    changefreq: "monthly",
    priority: 0.9,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    },
  },
  {
    path: "/methodology",
    title: "How the Dating Standards Calculator Works | Methodology & Probability Model",
    description:
      "How are your dating odds calculated? See the 10-factor probability model, why filters multiply, and how US Census and CDC data turn your standards into a number.",
    keywords:
      "how dating calculator works, dating standards methodology, probability calculation, dating odds formula, demographic data analysis, multiplicative model",
    changefreq: "monthly",
    priority: 0.8,
  },
  {
    path: "/data-sources",
    title: "Data Sources | US Census, CDC & Pew Research Behind the Calculator",
    description:
      "Every statistic in the partner calculator traces to a cited source: US Census ACS, CDC NHANES, and Pew Research. See the exact tables and studies behind your result.",
    keywords:
      "partner calculator data sources, US Census dating data, CDC NHANES body data, Pew Research dating, demographic statistics sources",
    changefreq: "yearly",
    priority: 0.7,
  },
  {
    path: "/glossary",
    title: "Dating Statistics Glossary | Probability, BMI, Percentile & More Defined",
    description:
      "Multiplicative probability, BMI distribution, conditional odds, percentile — every term in your calculator result explained in plain English. No statistics degree needed.",
    keywords: "dating statistics glossary, probability definitions, BMI distribution meaning, percentile definition, demographic terms explained",
    changefreq: "yearly",
    priority: 0.6,
  },
  {
    path: "/stats",
    title: "US Women & Dating Pool Statistics | Marriage, Income, Height & Education Data",
    description:
      "How many single women are there? What do they earn? Research-grade US dating pool statistics on marriage rates, income, education, height, and body type from Census, CDC, and Pew data.",
    keywords:
      "dating statistics, single women statistics, how many single women in the US, US demographics dating, dating pool data, marriage statistics, women income statistics",
    changefreq: "weekly",
    priority: 0.9,
  },
  {
    path: "/states",
    title: "Single Women by State | Dating Pool Size & Statistics for All 50 States",
    description:
      "How many single women are in your state? Browse dating pool estimates, education levels, and marriage statistics for women aged 18-44 in all 50 US states and DC.",
    keywords:
      "single women by state, dating pool by state, how many single women in my state, state demographics dating, women population by state",
    changefreq: "monthly",
    priority: 0.85,
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Dataset",
          name: "Dating Pool Demographics by US State",
          description:
            "Estimated women aged 18-44, median age, bachelor's degree attainment, and never-married rates for all 50 US states and DC.",
          url: `${SITE_URL}/states`,
          spatialCoverage: { "@type": "Country", name: "United States" },
          creator: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
        },
        faqPageSchema(statesFaq),
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "States", path: "/states" },
        ]),
      ],
    },
  },
  {
    path: "/about",
    title: "About Female Partner Calculator | Evidence-Based Dating Statistics",
    description:
      "Female Partner Calculator uses real US Census, CDC, and Pew Research data to calculate dating compatibility probabilities. Learn about the project, our principles, and the team behind the tool.",
    keywords:
      "about female partner calculator, dating statistics tool, evidence-based dating, demographic probability calculator, Summit Lane Digital",
    changefreq: "yearly",
    priority: 0.6,
  },
  {
    path: "/sitemap",
    title: "Site Map - Female Partner Calculator | All Pages & Resources",
    description:
      "Complete navigation guide to every page on Female Partner Calculator: calculators, statistics articles, state data, methodology, and FAQ.",
    noindex: true,
    changefreq: "monthly",
    priority: 0.5,
  },
  {
    path: "/privacy",
    title: "Privacy Policy - Female Partner Calculator | Data & Cookie Practices",
    description:
      "How Female Partner Calculator (operated by Summit Lane Digital, LLC) handles analytics, cookies, and any information collected through the site.",
    changefreq: "yearly",
    priority: 0.3,
  },
  {
    path: "/terms",
    title: "Terms of Service - Female Partner Calculator | User Agreement",
    description:
      "Terms of service for Female Partner Calculator, operated by Summit Lane Digital, LLC. Acceptable use, disclaimers, and limitations of liability.",
    changefreq: "yearly",
    priority: 0.3,
  },
];

function breadcrumbSchema(items: { name: string; path: string }[]): object {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}

export function articleRouteMeta(article: StatsArticle): RouteMeta {
  const path = `/stats/${article.slug}`;
  return {
    path,
    title: article.metaTitle,
    description: article.metaDescription,
    keywords: article.keywords,
    lastmod: article.datePublished,
    changefreq: "monthly",
    priority: 0.8,
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          headline: article.title,
          description: article.metaDescription,
          datePublished: article.datePublished,
          dateModified: article.datePublished,
          author: { "@type": "Organization", name: SITE_NAME },
          publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
          mainEntityOfPage: `${SITE_URL}${path}`,
        },
        {
          "@type": "FAQPage",
          mainEntity: article.faq.map((f) => ({
            "@type": "Question",
            name: f.question,
            acceptedAnswer: { "@type": "Answer", text: f.answer },
          })),
        },
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Statistics", path: "/stats" },
          { name: article.title, path },
        ]),
      ],
    },
  };
}

export function stateRouteMeta(state: StateInfo): RouteMeta {
  const path = `/states/${state.slug}`;
  const women = state.women18to44.toLocaleString("en-US");
  return {
    path,
    title: `Dating Pool in ${state.name}: Single Women Statistics (${state.abbreviation})`,
    description: `${state.name} has an estimated ${women} women aged 18-44. See education, marriage, and dating pool statistics for ${state.name}, then calculate your personal match probability.`,
    keywords: `dating in ${state.name}, single women ${state.name}, ${state.name} demographics, dating pool ${state.abbreviation}`,
    // Templated pages with ~190 unique words each; /states is the indexable hub.
    noindex: true,
    changefreq: "monthly",
    priority: 0.7,
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Dataset",
          name: `${state.name} Dating Pool Demographics`,
          description: `Demographic estimates for women aged 18-44 in ${state.name}: population, education, and marital status.`,
          spatialCoverage: { "@type": "State", name: state.name },
          creator: { "@type": "Organization", name: "US Census Bureau" },
        },
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "States", path: "/states" },
          { name: state.name, path },
        ]),
      ],
    },
  };
}

/** Every prerenderable route on the site. */
export function getAllRoutes(): RouteMeta[] {
  return [
    ...staticRoutes,
    ...statsArticles.map(articleRouteMeta),
    ...states.map(stateRouteMeta),
  ];
}

export function getRouteMeta(path: string): RouteMeta | undefined {
  return getAllRoutes().find((r) => r.path === path);
}

export function canonicalUrl(path: string): string {
  return path === "/" ? `${SITE_URL}/` : `${SITE_URL}${path}`;
}
